/**
 * アプリ内 URL パス定数。
 * 文字列リテラルでのパス直書き禁止 — 必ずここから参照する。
 */

// アプリのベースURL（絶対URL生成用）。NEXT_PUBLIC_*はNext.jsのビルド時に
// クライアント/サーバー双方へインライン化されるため、"use client"ファイルからも
// そのまま呼べる。sst.config.tsが全ステージで NEXT_PUBLIC_BASE_URL を注入する
// （NEXT_PUBLIC_APP_URL という別名は存在しない誤記——このヘルパー導入以前は
// 一部の呼び出し元がこの誤記の変数名を参照しており、dev/staging環境でも
// 常に本番URLへフォールバックしてしまっていた）。
export function getAppBaseUrl(): string {
  return process.env.NEXT_PUBLIC_BASE_URL ?? "https://proomi.prance.jp";
}

export const routes = {
  admin: {
    root: "/manage/admin",
    dashboard: "/manage/admin/dashboard",
    tenants: "/manage/admin/tenants",
    tenant: (id: string) => `/manage/admin/tenants/${id}`,
    users: "/manage/admin/users",
    superAdmins: "/manage/admin/super-admins",
    prompts: "/manage/admin/prompts",
    prompt: (promptId: string) => `/manage/admin/prompts/${promptId}`,
    llmSettings: "/manage/admin/llm-settings",
    systemSettings: "/manage/admin/system-settings",
    avatars: "/manage/admin/avatars",
    avatar: (avatarId: string) => `/manage/admin/avatars/${avatarId}`,
    guide: "/manage/admin/guide",
    auditLogs: "/manage/admin/audit-logs",
  },
  manage: {
    root: "/manage",
    usage: "/manage/usage",
    projects: "/manage/projects",
    projectNew: "/manage/projects/new",
    projectEdit: (projectId: string) => `/manage/projects/edit/${projectId}`,
    interviews: "/manage/interviews",
    interviewSession: (interviewId: string, sessionId: string) =>
      `/manage/interviews/${interviewId}/sessions/${sessionId}`,
    candidates: "/manage/candidates",
    screenings: "/manage/screenings",
    screening: (screeningId: string) => `/manage/screenings/${screeningId}`,
    organizations: "/manage/organizations",
    organization: (organizationId: string) =>
      `/manage/organizations/${organizationId}`,
    settings: "/manage/settings",
  },
  auth: {
    signIn: "/login",
    forgotPassword: "/login/forgot-password",
    resetPassword: (email?: string) =>
      email
        ? `/login/reset-password?email=${encodeURIComponent(email)}`
        : "/login/reset-password",
  },
  participants: {
    root: "/manage/participants",
    detail: (participantId: string) => `/manage/participants/${participantId}`,
  },
  rooms: {
    root: "/manage/rooms",
    new: "/manage/rooms/new",
    detail: (roomId: string) => `/manage/rooms/${roomId}`,
    edit: (roomId: string) => `/manage/rooms/edit/${roomId}`,
    bulkCreate: (templateRoomId: string) =>
      `/manage/rooms/bulk-create?templateId=${templateRoomId}`,
    session: (roomId: string, sessionId: string) =>
      `/manage/rooms/${roomId}/sessions/${sessionId}`,
    personas: "/manage/rooms/personas",
    personaNew: "/manage/rooms/personas/new",
    persona: (personaId: string) => `/manage/rooms/personas/${personaId}`,
    scenarios: "/manage/rooms/scenarios",
    scenarioNew: "/manage/rooms/scenarios/new",
    scenario: (scenarioId: string) => `/manage/rooms/scenarios/${scenarioId}`,
  },
  briefings: {
    root: "/manage/briefings",
    deck: (deckId: string) => `/manage/briefings/${deckId}`,
  },
  room: (shareUrlToken: string) => `/room/${shareUrlToken}`,
  sessionResult: (resultUrlToken: string) => `/session/${resultUrlToken}`,
  portal: {
    home: (organizationId: string) => `/portal/${organizationId}`,
    session: (organizationId: string, sessionId: string) =>
      `/portal/${organizationId}/sessions/${sessionId}`,
    login: (organizationId: string) => `/portal/${organizationId}/login`,
    forgotPassword: (organizationId: string) =>
      `/portal/${organizationId}/forgot-password`,
    resetPassword: (organizationId: string, email?: string) =>
      email
        ? `/portal/${organizationId}/reset-password?email=${encodeURIComponent(email)}`
        : `/portal/${organizationId}/reset-password`,
  },
  guide: "/manage/guide",
  apiDocs: "/manage/api-docs",
  docs: (lang: string) => `/docs/${lang}`,
  designSystem: "/design-system",
} as const;
